import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Business,
  Settings,
  Security,
  Storage,
  People,
  Description,
  Folder,
  AttachMoney,
  Assessment,
  Notifications,
  Palette,
  IntegrationInstructions,
  Info,
  Warning,
  CheckCircle,
  // Aliased: an unaliased `Error` import shadows the global constructor for the
  // whole module, so every `new Error(...)` below throws "is not a constructor".
  Error as ErrorIcon,
  Edit,
  Save,
  Cancel,
  Refresh,
  Add,
  ContentCopy,
  PersonAdd,
  Block,
  PlayArrow,
  Archive,
  DeleteForever,
  EditCalendar,
  Gavel,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage, messageFromApiBody, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';
import TenantContractDialog, { ContractTenant } from './TenantContractDialog';
import TenantCourtFormAccessDialog from './TenantCourtFormAccessDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface PlatformTenantRow {
  id: string;
  tenantNumber: string;
  name: string;
  subdomain: string;
  status: string;
  type: string;
  plan: string;
  isActive: boolean;
  userCount: number;
  createdAt: string;
  readOnlyMode?: boolean;
  trialLifecycleState?: string | null;
  subscription?: any;
  metadata?: any;
  isTrialExpired?: boolean;
  daysUntilTrialExpiry?: number | null;
}

// Mirrors slugifySubdomain in backend/src/services/tenantService.js so the field
// can never hold a value the API will reject.
const slugifySubdomain = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 100);

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tenant-tabpanel-${index}`}
      aria-labelledby={`tenant-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SsoConfigCard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    ok: boolean;
    errors: string[];
    warnings: string[];
  } | null>(null);
  const [config, setConfig] = useState({
    planAllowsSso: false,
    enabled: false,
    protocol: 'oidc',
    issuer: '',
    entryPoint: '',
    idpCert: '',
    authorizationUrl: '',
    tokenUrl: '',
    userInfoUrl: '',
    clientId: '',
    clientSecret: '',
    scopes: 'openid email profile',
    callbackUrl: '',
    clientSecretSet: false,
    idpCertSet: false,
    entityId: '',
    audience: '',
    logoutUrl: '',
    attributeMapJson: '',
    wantAssertionsSigned: true,
    digestAlgorithm: 'sha256',
    signatureAlgorithm: 'sha256',
    samlAcsUrl: '',
    samlMetadataUrl: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/api/sso/config', {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const json = await res.json();
        if (res.ok && json.data) {
          setConfig((prev) => ({
            ...prev,
            ...json.data,
            clientSecret: '',
            idpCert: '',
            attributeMapJson: json.data.attributeMap
              ? JSON.stringify(json.data.attributeMap, null, 2)
              : '',
          }));
        }
      } catch (_) { /* ignore */ }
      finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setValidationResult(null);
    try {
      const payload: Record<string, unknown> = {
        enabled: config.enabled,
        protocol: config.protocol,
        issuer: config.issuer,
        scopes: config.scopes,
      };
      if (config.protocol === 'saml') {
        payload.entryPoint = config.entryPoint;
        payload.entityId = config.entityId;
        payload.audience = config.audience;
        payload.logoutUrl = config.logoutUrl;
        payload.wantAssertionsSigned = config.wantAssertionsSigned;
        payload.digestAlgorithm = config.digestAlgorithm;
        payload.signatureAlgorithm = config.signatureAlgorithm;
        if (config.idpCert) payload.idpCert = config.idpCert;
        if (config.attributeMapJson.trim()) {
          try {
            payload.attributeMap = JSON.parse(config.attributeMapJson);
          } catch {
            throw new Error('Attribute map must be valid JSON');
          }
        }
      } else {
        payload.authorizationUrl = config.authorizationUrl;
        payload.tokenUrl = config.tokenUrl;
        payload.userInfoUrl = config.userInfoUrl;
        payload.clientId = config.clientId;
        if (config.clientSecret) payload.clientSecret = config.clientSecret;
      }
      const res = await fetch('/api/sso/config', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Failed to save SSO config'));
      }
      setConfig((prev) => ({
        ...prev,
        clientSecret: '',
        idpCert: '',
        clientSecretSet: config.protocol === 'oidc' ? true : prev.clientSecretSet,
        idpCertSet: config.protocol === 'saml' ? true : prev.idpCertSet,
      }));
      alert('SSO configuration saved');
    } catch (err) {
      alert(toUserErrorMessage(err, 'Failed to save SSO'));
    } finally {
      setSaving(false);
    }
  };

  const validateSaml = async () => {
    setValidating(true);
    setValidationResult(null);
    try {
      const res = await fetch('/api/sso/saml/validate', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'SAML validation failed'));
      }
      const json = await res.json();
      setValidationResult(json.data);
    } catch (err) {
      alert(toUserErrorMessage(err, 'SAML validation failed'));
    } finally {
      setValidating(false);
    }
  };

  const isSaml = config.protocol === 'saml';

  if (loading) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Loading SSO configuration…
      </Alert>
    );
  }

  if (!config.planAllowsSso) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        SSO (OIDC) is available on Enterprise plans.
      </Alert>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          SSO Identity Provider
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Staff login with SSO uses <code>/login?tenant=your-subdomain</code>.
          {isSaml ? (
            <> ACS URL: <strong>{config.samlAcsUrl || '(load after save)'}</strong></>
          ) : (
            <> Callback URL: <strong>{config.callbackUrl || '(load after save)'}</strong></>
          )}
        </Alert>
        {validationResult && (
          <Alert severity={validationResult.ok ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {validationResult.ok ? 'SAML configuration looks valid.' : 'SAML configuration has issues.'}
            {validationResult.errors.length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                {validationResult.errors.map((e) => <li key={e}>{e}</li>)}
              </Box>
            )}
            {validationResult.warnings.length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                {validationResult.warnings.map((w) => <li key={w}>{w}</li>)}
              </Box>
            )}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                disabled={user?.role !== 'admin'}
              />
              <Typography>Enable SSO</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={user?.role !== 'admin'}>
              <InputLabel>Protocol</InputLabel>
              <Select
                label="Protocol"
                value={config.protocol}
                onChange={(e) => setConfig({ ...config, protocol: e.target.value })}
              >
                <MenuItem value="oidc">OpenID Connect (OIDC)</MenuItem>
                <MenuItem value="saml">SAML 2.0</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Issuer"
              value={config.issuer}
              onChange={(e) => setConfig({ ...config, issuer: e.target.value })}
              disabled={user?.role !== 'admin'}
              helperText={isSaml ? 'IdP entity ID / issuer URI' : 'OIDC issuer URL'}
            />
          </Grid>

          {isSaml ? (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Entry point (SSO URL)"
                  value={config.entryPoint}
                  onChange={(e) => setConfig({ ...config, entryPoint: e.target.value })}
                  disabled={user?.role !== 'admin'}
                  placeholder="https://idp.example.com/saml/sso"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  label={config.idpCertSet ? 'IdP certificate (leave blank to keep)' : 'IdP certificate (PEM)'}
                  value={config.idpCert}
                  onChange={(e) => setConfig({ ...config, idpCert: e.target.value })}
                  disabled={user?.role !== 'admin'}
                  placeholder="-----BEGIN CERTIFICATE-----"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SP audience / entity ID"
                  value={config.audience}
                  onChange={(e) => setConfig({ ...config, audience: e.target.value })}
                  disabled={user?.role !== 'admin'}
                  helperText="Service provider entity ID for IdP configuration"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Entity ID (alias)"
                  value={config.entityId}
                  onChange={(e) => setConfig({ ...config, entityId: e.target.value })}
                  disabled={user?.role !== 'admin'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="IdP logout URL (SLO)"
                  value={config.logoutUrl}
                  onChange={(e) => setConfig({ ...config, logoutUrl: e.target.value })}
                  disabled={user?.role !== 'admin'}
                  placeholder="https://idp.example.com/saml/slo"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Attribute map (JSON, optional)"
                  value={config.attributeMapJson}
                  onChange={(e) => setConfig({ ...config, attributeMapJson: e.target.value })}
                  disabled={user?.role !== 'admin'}
                  placeholder={'{\n  "email": "email",\n  "firstName": "givenName",\n  "lastName": "surname",\n  "groups": "groups"\n}'}
                  helperText="Maps SAML attributes to email, firstName, lastName, groups"
                />
              </Grid>
              {config.samlMetadataUrl && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    SP metadata: <code>{config.samlMetadataUrl}</code>
                  </Typography>
                </Grid>
              )}
            </>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Client ID"
                  value={config.clientId}
                  onChange={(e) => setConfig({ ...config, clientId: e.target.value })}
                  disabled={user?.role !== 'admin'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Authorization URL"
                  value={config.authorizationUrl}
                  onChange={(e) => setConfig({ ...config, authorizationUrl: e.target.value })}
                  disabled={user?.role !== 'admin'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Token URL"
                  value={config.tokenUrl}
                  onChange={(e) => setConfig({ ...config, tokenUrl: e.target.value })}
                  disabled={user?.role !== 'admin'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="UserInfo URL (optional)"
                  value={config.userInfoUrl}
                  onChange={(e) => setConfig({ ...config, userInfoUrl: e.target.value })}
                  disabled={user?.role !== 'admin'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={config.clientSecretSet ? 'Client Secret (leave blank to keep)' : 'Client Secret'}
                  type="password"
                  value={config.clientSecret}
                  onChange={(e) => setConfig({ ...config, clientSecret: e.target.value })}
                  disabled={user?.role !== 'admin'}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={save}
                disabled={user?.role !== 'admin' || saving}
              >
                {saving ? 'Saving…' : 'Save SSO settings'}
              </Button>
              {isSaml && (
                <Button
                  variant="outlined"
                  startIcon={validating ? <CircularProgress size={18} /> : <CheckCircle />}
                  onClick={validateSaml}
                  disabled={user?.role !== 'admin' || validating}
                >
                  {validating ? 'Validating…' : 'Validate SAML config'}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const TenantPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [settings, setSettings] = useState<any>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'branding' | 'limits' | 'features' | null>(null);
  const [platformTenants, setPlatformTenants] = useState<PlatformTenantRow[]>([]);
  const [contractTenant, setContractTenant] = useState<ContractTenant | null>(null);
  const [formAccessTenant, setFormAccessTenant] = useState<{ id: string; name: string; plan: string } | null>(null);
  const closeFormAccess = useCallback(() => setFormAccessTenant(null), []);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformError, setPlatformError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createResult, setCreateResult] = useState<{
    inviteUrl?: string;
    email?: string;
    emailSent?: boolean;
    firmName?: string;
    password?: string;
    bypassUsed?: boolean;
  } | null>(null);
  const [demoBypassEnabled, setDemoBypassEnabled] = useState(false);
  // Until the platform admin types in the Subdomain field themselves, it keeps
  // tracking the firm name. Testing `subdomain` for emptiness instead froze it
  // at the first character of the name, which the API then rejected as too short.
  const [subdomainEdited, setSubdomainEdited] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    subdomain: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    type: 'trial',
    plan: 'basic',
    status: 'pending',
    trialDays: 14,
    seatCap: 10,
    billingMode: 'none',
    seedDemoData: false,
    practiceFocus: '',
    dealId: '',
    internalOwner: '',
    sendInvite: true,
    demoBypassInvite: false,
  });

  const { tenant, isLoading, error, refreshTenant, updateTenantSettings, getFeatureStatus, getUsagePercentage, isWithinLimits } = useTenant();
  const { user, token } = useAuth();
  const isPlatformAdmin = Boolean(user?.isPlatformAdmin);

  const loadPlatformTenants = useCallback(async () => {
    if (!isPlatformAdmin || !token) return;
    setPlatformLoading(true);
    setPlatformError(null);
    try {
      const response = await fetch('/api/admin/super/tenants?limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to load platform tenants');
      }
      setPlatformTenants(data.data?.tenants || []);
      setDemoBypassEnabled(Boolean(data.data?.demoBypassEnabled));
    } catch (err: any) {
      setPlatformError(err.message || 'Failed to load platform tenants');
      setPlatformTenants([]);
    } finally {
      setPlatformLoading(false);
    }
  }, [isPlatformAdmin, token]);

  useEffect(() => {
    loadPlatformTenants();
  }, [loadPlatformTenants]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditSettings = (type: 'branding' | 'limits' | 'features') => {
    setDialogType(type);
    setSettings(tenant?.settings || {});
    setIsDialogOpen(true);
  };

  const handleSaveSettings = async () => {
    try {
      await updateTenantSettings(settings);
      setIsDialogOpen(false);
      setEditMode(false);
      await refreshTenant();
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsDialogOpen(false);
    setEditMode(false);
    setSettings(tenant?.settings || {});
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'suspended':
        return 'warning';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'enterprise':
        return 'success';
      case 'paid':
        return 'primary';
      case 'trial':
        return 'warning';
      case 'demo':
        return 'info';
      default:
        return 'default';
    }
  };

  const renderOverview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tenant Information
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Name: {tenant?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Subdomain: {tenant?.subdomain}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Domain: {tenant?.domain || 'Not configured'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                label={tenant?.status}
                color={getStatusColor(tenant?.status || '') as any}
                size="small"
              />
              <Chip
                label={tenant?.type}
                color={getTypeColor(tenant?.type || '') as any}
                size="small"
              />
              <Chip
                label={tenant?.plan}
                variant="outlined"
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(tenant?.createdAt || '').toLocaleDateString()}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Usage Statistics
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Users</Typography>
                <Typography variant="body2">
                  {tenant?.usage?.users || 0} / {tenant?.settings?.limits?.users || 0}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getUsagePercentage('users')}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Clients</Typography>
                <Typography variant="body2">
                  {tenant?.usage?.clients || 0} / {tenant?.settings?.limits?.clients || 0}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getUsagePercentage('clients')}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Cases</Typography>
                <Typography variant="body2">
                  {tenant?.usage?.cases || 0} / {tenant?.settings?.limits?.cases || 0}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getUsagePercentage('cases')}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Documents</Typography>
                <Typography variant="body2">
                  {tenant?.usage?.documents || 0} / {tenant?.settings?.limits?.documents || 0}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getUsagePercentage('documents')}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Storage</Typography>
                <Typography variant="body2">
                  {((tenant?.usage?.storageUsed || 0) / (1024 * 1024 * 1024)).toFixed(2)} GB / {tenant?.settings?.limits?.storageGB || 0} GB
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getUsagePercentage('storageUsed')}
                sx={{ mb: 2 }}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Multi-Tenant Isolation Status
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="Tenant Isolation Active"
                  secondary="All data is properly isolated by tenant ID"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="User Access Control"
                  secondary="Users can only access data within their tenant"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="Data Segregation"
                  secondary="Clients, cases, documents, and billing are tenant-specific"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="API Isolation"
                  secondary="All API endpoints enforce tenant boundaries"
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderBranding = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {(tenant?.plan !== 'enterprise' && tenant?.plan !== 'custom' && !tenant?.settings?.features?.whiteLabel) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Custom logo and theme colors require an <strong>Enterprise</strong> plan (white-label).
            Upgrade under Subscription to unlock branding.
          </Alert>
        )}
        {(tenant?.plan === 'enterprise' || tenant?.plan === 'custom' || tenant?.settings?.features?.sso) && (
          <SsoConfigCard />
        )}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Branding Settings
              </Typography>
              <Button
                startIcon={<Edit />}
                onClick={() => handleEditSettings('branding')}
                disabled={user?.role !== 'admin'}
              >
                Edit Branding
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Company Information
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Company Name: {tenant?.settings?.branding?.companyName || 'Not set'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Website: {tenant?.settings?.branding?.website || 'Not set'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Color Scheme
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Primary Color
                    </Typography>
                    <Box
                      sx={{
                        width: 40,
                        height: 20,
                        backgroundColor: tenant?.settings?.branding?.primaryColor || '#1976d2',
                        border: '1px solid #ccc',
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Secondary Color
                    </Typography>
                    <Box
                      sx={{
                        width: 40,
                        height: 20,
                        backgroundColor: tenant?.settings?.branding?.secondaryColor || '#dc004e',
                        border: '1px solid #ccc',
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFeatures = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Feature Management
              </Typography>
              <Button
                startIcon={<Edit />}
                onClick={() => handleEditSettings('features')}
                disabled={user?.role !== 'admin'}
              >
                Edit Features
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Core Features
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      {getFeatureStatus('documentManagement') ? <CheckCircle color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText
                      primary="Document Management"
                      secondary={getFeatureStatus('documentManagement') ? 'Enabled' : 'Disabled'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {getFeatureStatus('caseManagement') ? <CheckCircle color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText
                      primary="Case Management"
                      secondary={getFeatureStatus('caseManagement') ? 'Enabled' : 'Disabled'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {getFeatureStatus('clientPortal') ? <CheckCircle color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText
                      primary="Client Portal"
                      secondary={getFeatureStatus('clientPortal') ? 'Enabled' : 'Disabled'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {getFeatureStatus('billing') ? <CheckCircle color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText
                      primary="Billing & Invoicing"
                      secondary={getFeatureStatus('billing') ? 'Enabled' : 'Disabled'}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Advanced Features
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      {getFeatureStatus('reporting') ? <CheckCircle color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText
                      primary="Reporting & Analytics"
                      secondary={getFeatureStatus('reporting') ? 'Enabled' : 'Disabled'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {getFeatureStatus('emailIntegration') ? <CheckCircle color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText
                      primary="Email Integration"
                      secondary={getFeatureStatus('emailIntegration') ? 'Enabled' : 'Disabled'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {getFeatureStatus('calendarIntegration') ? <CheckCircle color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText
                      primary="Calendar Integration"
                      secondary={getFeatureStatus('calendarIntegration') ? 'Enabled' : 'Disabled'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {getFeatureStatus('paymentGateway') ? <CheckCircle color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText
                      primary="Client invoice payments"
                      secondary={getFeatureStatus('paymentGateway') ? 'Enabled — portal Pay on invoices' : 'Disabled'}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderLimits = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Usage Limits
              </Typography>
              <Button
                startIcon={<Edit />}
                onClick={() => handleEditSettings('limits')}
                disabled={user?.role !== 'admin'}
              >
                Edit Limits
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  User Limits
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Maximum Users"
                      secondary={tenant?.settings?.limits?.users || 0}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Current Users"
                      secondary={tenant?.usage?.users || 0}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Data Limits
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Maximum Cases"
                      secondary={tenant?.settings?.limits?.cases || 0}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Maximum Clients"
                      secondary={tenant?.settings?.limits?.clients || 0}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Maximum Documents"
                      secondary={tenant?.settings?.limits?.documents || 0}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Storage Limit"
                      secondary={`${tenant?.settings?.limits?.storageGB || 0} GB`}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const openCreateWizard = () => {
    setCreateResult(null);
    setSubdomainEdited(false);
    setCreateForm({
      name: '',
      subdomain: '',
      adminEmail: '',
      adminFirstName: '',
      adminLastName: '',
      type: 'trial',
      plan: 'basic',
      status: 'pending',
      trialDays: 14,
      seatCap: 10,
      billingMode: 'none',
      seedDemoData: false,
      practiceFocus: '',
      dealId: '',
      internalOwner: '',
      sendInvite: true,
      demoBypassInvite: false,
    });
    setCreateOpen(true);
  };

  const handleCreateTenant = async () => {
    if (!token) return;
    setCreateSubmitting(true);
    try {
      const res = await fetch('/api/admin/super/tenants', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...createForm,
          seatCap: createForm.seatCap || undefined,
          practiceFocus: createForm.practiceFocus || undefined,
          dealId: createForm.dealId || undefined,
          internalOwner: createForm.internalOwner || undefined,
          subdomain: createForm.subdomain || undefined,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(messageFromApiBody(json, 'Failed to create tenant'));
      }
      setCreateResult({
        inviteUrl: json.data?.admin?.inviteUrl,
        email: json.data?.admin?.email,
        emailSent: json.data?.admin?.emailSent,
        firmName: json.data?.tenant?.name,
        password: json.data?.admin?.password,
        bypassUsed: Boolean(json.data?.bypassUsed),
      });
      toast.success(json.message || 'Tenant created');
      await loadPlatformTenants();
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Failed to create tenant'));
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleResendInvite = async (tenantId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/admin/super/tenants/${tenantId}/resend-invite`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(messageFromApiBody(json, 'Failed to resend invite'));
      }
      if (json.data?.inviteUrl) {
        await navigator.clipboard.writeText(json.data.inviteUrl);
        toast.success(json.data.emailSent ? 'Invite resent (link copied)' : 'Invite link copied');
      } else {
        toast.success(json.message || 'Invite resent');
      }
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Failed to resend invite'));
    }
  };

  const runTenantLifecycleAction = async (
    tenantId: string,
    action: 'suspend' | 'activate' | 'archive' | 'delete',
  ) => {
    if (!token) return;
    const labels = {
      suspend: 'Suspend',
      activate: 'Activate',
      archive: 'Archive',
      delete: 'Delete',
    } as const;
    if (action === 'delete') {
      const ok = window.confirm(
        'Permanently soft-delete this tenant and clean up users, clients, cases, and documents? This cannot be undone from the UI.',
      );
      if (!ok) return;
    } else if (action === 'archive') {
      const ok = window.confirm('Archive this tenant? Users will lose access; data is retained.');
      if (!ok) return;
    } else if (action === 'suspend') {
      const ok = window.confirm('Suspend this tenant? Users will be blocked from logging in.');
      if (!ok) return;
    }

    try {
      let res: Response;
      if (action === 'delete') {
        res = await fetch(`/api/admin/super/tenants/${tenantId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        res = await fetch(`/api/admin/super/tenants/${tenantId}/${action}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(messageFromApiBody(json, `${labels[action]} failed`));
      }
      if (json.warning) toast(json.warning, { icon: '⚠️', duration: 8000 });
      else toast.success(json.message || `${labels[action]} succeeded`);
      await loadPlatformTenants();
    } catch (err) {
      toast.error(toUserErrorMessage(err, `${labels[action]} failed`));
    }
  };

  const renderPlatformTenants = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Platform operator view — create Soft Launch / pilot firm workspaces and invite the first firm administrator.
      </Alert>
      {platformError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {platformError}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6">
          All Tenants ({platformTenants.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<Refresh />}
            onClick={loadPlatformTenants}
            variant="outlined"
            size="small"
            disabled={platformLoading}
          >
            Refresh list
          </Button>
          <Button
            startIcon={<Add />}
            onClick={openCreateWizard}
            variant="contained"
            size="small"
          >
            Create Tenant
          </Button>
        </Box>
      </Box>
      {platformLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Subdomain</TableCell>
                <TableCell>Number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Access</TableCell>
                <TableCell align="right">Users</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {platformTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography color="text.secondary">No tenants found in this database.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                platformTenants.map((row) => (
                  <TableRow key={row.id} hover selected={row.id === tenant?.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.subdomain}</TableCell>
                    <TableCell>{row.tenantNumber}</TableCell>
                    <TableCell>
                      <Chip label={row.status} size="small" color={getStatusColor(row.status) as any} />
                    </TableCell>
                    <TableCell>
                      <Chip label={row.type} size="small" color={getTypeColor(row.type) as any} />
                    </TableCell>
                    <TableCell>{row.plan}</TableCell>
                    <TableCell>
                      {row.readOnlyMode ? (
                        <Chip label="Read-only" size="small" color="error" />
                      ) : row.trialLifecycleState === 'grace' ? (
                        <Chip label="Grace period" size="small" color="warning" />
                      ) : (row.type === 'trial' || row.type === 'demo') && row.daysUntilTrialExpiry != null ? (
                        <Chip
                          label={row.daysUntilTrialExpiry < 0 ? 'Trial expired' : `Trial: ${row.daysUntilTrialExpiry}d left`}
                          size="small"
                          variant="outlined"
                          color={row.daysUntilTrialExpiry <= 3 ? 'warning' : 'default'}
                        />
                      ) : (
                        <Chip label="Full access" size="small" variant="outlined" color="success" />
                      )}
                    </TableCell>
                    <TableCell align="right">{row.userCount}</TableCell>
                    <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit contract (reactivate, extend, change plan)">
                        <IconButton size="small" color={row.readOnlyMode ? 'error' : 'primary'} onClick={() => setContractTenant(row as ContractTenant)}>
                          <EditCalendar fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Court form access (Federal / states)">
                        <IconButton size="small" onClick={() => setFormAccessTenant({ id: row.id, name: row.name, plan: row.plan })}>
                          <Gavel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Resend firm admin invite">
                        <IconButton size="small" onClick={() => handleResendInvite(row.id)}>
                          <PersonAdd fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {row.status === 'suspended' || row.status === 'inactive' ? (
                        <Tooltip title="Activate tenant">
                          <IconButton size="small" color="success" onClick={() => runTenantLifecycleAction(row.id, 'activate')}>
                            <PlayArrow fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Suspend tenant">
                          <IconButton size="small" color="warning" onClick={() => runTenantLifecycleAction(row.id, 'suspend')}>
                            <Block fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.status !== 'inactive' && (
                        <Tooltip title="Archive tenant (retain data)">
                          <IconButton size="small" onClick={() => runTenantLifecycleAction(row.id, 'archive')}>
                            <Archive fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete tenant">
                        <IconButton size="small" color="error" onClick={() => runTenantLifecycleAction(row.id, 'delete')}>
                          <DeleteForever fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TenantContractDialog tenant={contractTenant} onClose={() => setContractTenant(null)} onSaved={loadPlatformTenants} />
      <TenantCourtFormAccessDialog tenant={formAccessTenant} onClose={closeFormAccess} onSaved={loadPlatformTenants} />
      <Dialog open={createOpen} onClose={() => !createSubmitting && setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Tenant</DialogTitle>
        <DialogContent dividers>
          {createResult ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Workspace created for <strong>{createResult.firmName}</strong>
                {createResult.bypassUsed
                  ? `. Demo bypass used — no email verification needed. ${createResult.email} can log in immediately with the temporary password below.`
                  : (createResult.emailSent
                    ? `. Invite emailed to ${createResult.email}.`
                    : `. Invite email was not sent — copy the link for ${createResult.email}.`)}
              </Alert>
              {createResult.bypassUsed && createResult.password && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Temporary password for ${createResult.email}`}
                    value={createResult.password}
                    InputProps={{ readOnly: true }}
                    helperText="Demo/testing only — admin must change this on first login."
                  />
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={async () => {
                      await navigator.clipboard.writeText(createResult.password || '');
                      toast.success('Password copied');
                    }}
                  >
                    Copy
                  </Button>
                </Box>
              )}
              {createResult.inviteUrl && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Firm admin invite link"
                    value={createResult.inviteUrl}
                    InputProps={{ readOnly: true }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={async () => {
                      await navigator.clipboard.writeText(createResult.inviteUrl || '');
                      toast.success('Invite link copied');
                    }}
                  >
                    Copy
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Firm name"
                  value={createForm.name}
                  onChange={(e) => setCreateForm((f) => ({
                    ...f,
                    name: e.target.value,
                    subdomain: subdomainEdited ? f.subdomain : slugifySubdomain(e.target.value),
                  }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subdomain"
                  error={createForm.subdomain.length === 1}
                  helperText={
                    createForm.subdomain.length === 1
                      ? 'At least 2 characters'
                      : 'Letters, numbers, hyphens'
                  }
                  value={createForm.subdomain}
                  onChange={(e) => {
                    // Keep a trailing hyphen the admin is still typing; the server
                    // slugify trims it. Only reject characters outright.
                    const next = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, '-')
                      .replace(/-+/g, '-')
                      .slice(0, 100);
                    setSubdomainEdited(next.length > 0);
                    setCreateForm((f) => ({
                      ...f,
                      subdomain: next || slugifySubdomain(f.name),
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Firm admin email"
                  type="email"
                  value={createForm.adminEmail}
                  onChange={(e) => setCreateForm((f) => ({ ...f, adminEmail: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Admin first name"
                  value={createForm.adminFirstName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, adminFirstName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Admin last name"
                  value={createForm.adminLastName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, adminLastName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={createForm.type}
                    onChange={(e) => setCreateForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    <MenuItem value="trial">Trial / Pilot</MenuItem>
                    <MenuItem value="demo">Demo</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Plan</InputLabel>
                  <Select
                    label="Plan"
                    value={createForm.plan}
                    onChange={(e) => setCreateForm((f) => ({ ...f, plan: e.target.value }))}
                  >
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={createForm.status}
                    onChange={(e) => setCreateForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    <MenuItem value="pending">Pending (pilot)</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="demo">Demo</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Billing</InputLabel>
                  <Select
                    label="Billing"
                    value={createForm.billingMode}
                    onChange={(e) => setCreateForm((f) => ({ ...f, billingMode: e.target.value }))}
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="manual">Manual</MenuItem>
                    <MenuItem value="stripe">Stripe later</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Trial days"
                  value={createForm.trialDays}
                  onChange={(e) => setCreateForm((f) => ({ ...f, trialDays: Number(e.target.value) || 14 }))}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Seat cap"
                  value={createForm.seatCap}
                  onChange={(e) => setCreateForm((f) => ({ ...f, seatCap: Number(e.target.value) || 0 }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Practice focus"
                  value={createForm.practiceFocus}
                  onChange={(e) => setCreateForm((f) => ({ ...f, practiceFocus: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="CRM deal ID"
                  value={createForm.dealId}
                  onChange={(e) => setCreateForm((f) => ({ ...f, dealId: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Internal owner"
                  value={createForm.internalOwner}
                  onChange={(e) => setCreateForm((f) => ({ ...f, internalOwner: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color={createForm.demoBypassInvite ? 'text.disabled' : 'text.primary'}>
                      Email invite to firm admin
                    </Typography>
                    <Switch
                      checked={createForm.sendInvite && !createForm.demoBypassInvite}
                      disabled={createForm.demoBypassInvite}
                      onChange={(e) => setCreateForm((f) => ({ ...f, sendInvite: e.target.checked }))}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Seed demo sample data</Typography>
                    <Switch
                      checked={createForm.seedDemoData}
                      onChange={(e) => setCreateForm((f) => ({ ...f, seedDemoData: e.target.checked }))}
                    />
                  </Box>
                </Box>
              </Grid>
              {demoBypassEnabled && (
                <Grid item xs={12}>
                  <Alert
                    severity={createForm.demoBypassInvite ? 'warning' : 'info'}
                    sx={{ alignItems: 'center' }}
                    action={
                      <Switch
                        checked={createForm.demoBypassInvite}
                        onChange={(e) => setCreateForm((f) => ({ ...f, demoBypassInvite: e.target.checked }))}
                      />
                    }
                  >
                    <strong>Demo bypass — skip email verification.</strong> No SMTP is configured in this
                    environment. When enabled, the firm admin is created active immediately with a
                    temporary password shown after creation (no invite link, no email required). This
                    testing-only bypass is hard-disabled in production regardless of this toggle.
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createSubmitting}>
            {createResult ? 'Close' : 'Cancel'}
          </Button>
          {!createResult && (
            <Button
              variant="contained"
              onClick={handleCreateTenant}
              disabled={
                createSubmitting
                || !createForm.name.trim()
                || !createForm.adminEmail.trim()
              }
            >
              {createSubmitting ? <CircularProgress size={20} /> : 'Create & invite'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading tenant information...
        </Typography>
      </Box>
    );
  }

  if (error && !isPlatformAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading tenant information: {error}
        </Alert>
      </Box>
    );
  }

  if (!tenant && !isPlatformAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No tenant information available.
        </Alert>
      </Box>
    );
  }

  const settingsTabOffset = isPlatformAdmin ? 1 : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Business sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Tenant Management
        </Typography>
        {isPlatformAdmin && (
          <Chip label="Platform Master" color="secondary" size="small" />
        )}
        <Button
          startIcon={<Refresh />}
          onClick={() => {
            refreshTenant();
            loadPlatformTenants();
          }}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Current-tenant settings failed to load: {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="tenant tabs">
          {isPlatformAdmin && (
            <Tab label="All Tenants" icon={<People />} iconPosition="start" />
          )}
          <Tab label="Overview" icon={<Info />} iconPosition="start" disabled={!tenant} />
          <Tab label="Branding" icon={<Palette />} iconPosition="start" disabled={!tenant} />
          <Tab label="Features" icon={<Settings />} iconPosition="start" disabled={!tenant} />
          <Tab label="Limits" icon={<Storage />} iconPosition="start" disabled={!tenant} />
        </Tabs>
      </Box>

      {isPlatformAdmin && (
        <TabPanel value={tabValue} index={0}>
          {renderPlatformTenants()}
        </TabPanel>
      )}
      <TabPanel value={tabValue} index={0 + settingsTabOffset}>
        {tenant ? renderOverview() : <Alert severity="warning">No current tenant loaded.</Alert>}
      </TabPanel>
      <TabPanel value={tabValue} index={1 + settingsTabOffset}>
        {tenant ? renderBranding() : null}
      </TabPanel>
      <TabPanel value={tabValue} index={2 + settingsTabOffset}>
        {tenant ? renderFeatures() : null}
      </TabPanel>
      <TabPanel value={tabValue} index={3 + settingsTabOffset}>
        {tenant ? renderLimits() : null}
      </TabPanel>

      {/* Settings Dialog */}
      <Dialog open={isDialogOpen} onClose={handleCancelEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit {dialogType === 'branding' ? 'Branding' : dialogType === 'features' ? 'Features' : 'Limits'} Settings
        </DialogTitle>
        <DialogContent>
          {dialogType === 'branding' && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={settings?.branding?.companyName || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    branding: { ...settings.branding, companyName: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={settings?.branding?.website || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    branding: { ...settings.branding, website: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ minWidth: 100 }}>
                    Primary Color
                  </Typography>
                  <input
                    type="color"
                    value={settings?.branding?.primaryColor || '#1976d2'}
                    onChange={(e) => setSettings({
                      ...settings,
                      branding: { ...settings.branding, primaryColor: e.target.value }
                    })}
                    style={{
                      width: '50px',
                      height: '40px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  />
                  <TextField
                    size="small"
                    value={settings?.branding?.primaryColor || '#1976d2'}
                    onChange={(e) => setSettings({
                      ...settings,
                      branding: { ...settings.branding, primaryColor: e.target.value }
                    })}
                    sx={{ width: 120 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ minWidth: 100 }}>
                    Secondary Color
                  </Typography>
                  <input
                    type="color"
                    value={settings?.branding?.secondaryColor || '#dc004e'}
                    onChange={(e) => setSettings({
                      ...settings,
                      branding: { ...settings.branding, secondaryColor: e.target.value }
                    })}
                    style={{
                      width: '50px',
                      height: '40px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  />
                  <TextField
                    size="small"
                    value={settings?.branding?.secondaryColor || '#dc004e'}
                    onChange={(e) => setSettings({
                      ...settings,
                      branding: { ...settings.branding, secondaryColor: e.target.value }
                    })}
                    sx={{ width: 120 }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}

          {dialogType === 'features' && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {Object.entries(settings?.features || {}).map(([feature, enabled]) => (
                <Grid item xs={12} md={6} key={feature}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      {feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </Typography>
                    <Switch
                      checked={enabled as boolean}
                      onChange={(e) => setSettings({
                        ...settings,
                        features: { ...settings.features, [feature]: e.target.checked }
                      })}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {dialogType === 'limits' && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {Object.entries(settings?.limits || {}).map(([limit, value]) => (
                <Grid item xs={12} md={6} key={limit}>
                  <TextField
                    fullWidth
                    label={limit.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    type="number"
                    value={value as number}
                    onChange={(e) => setSettings({
                      ...settings,
                      limits: { ...settings.limits, [limit]: parseInt(e.target.value) || 0 }
                    })}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button onClick={handleSaveSettings} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantPage; 